import React, { useState } from 'react';
import { Modal } from '@ofself/shell';
import { Facts, Note } from '../../components/nucleus/Inspector';
import Segmented from '../../components/nucleus/Segmented';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import { useConfirm } from '../../components/nucleus/actions';
import { usePlace } from '../../nucleus/place';
import { pluginsApi } from './data/api';
import { accessItem } from './data/items';
import { ACCESS_MODES, ACCESS_WORD, toolAsksLine } from './data/words';
import './plugin-inspector.css';

// TOOL ACCESS — what one app or agent may do with each tool of one plugin:
// Allowed, Asks you or Never (plugin-words.md, "The rule"). Giving an app a
// tool is all it needs: the tool reads and writes what it asks for, in the
// app's realm, and the app sees only what its own access allows. Each tool
// says what it reads and writes and whether it can't be undone; Allowed on one
// that can't be undone asks first. Saves as it changes (PUT …/access/<ep_id>,
// the tool it is given; the others keep what they had). A plugin ofSelf hasn't
// verified has no rows: apps and agents can use only verified plugins' tools.
// The contract itself is a door away.

const OPTIONS = ACCESS_MODES.map((m) => ({ id: m, label: ACCESS_WORD[m] }));

/** Said wherever an unverified plugin's tool access would be. */
export const NOT_VERIFIED = 'Apps and agents can use only tools of plugins Verified by ofSelf.';

/** A row in the plugin's Tool access band: "Notebook · runs in Everyday". */
export const accessRowName = (g) => (g.realm?.name ? `${g.name} · runs in ${g.realm.name}` : g.name);

/** The sheet's one instruction line: what giving this app a tool means. */
export function accessRule(g) {
    const app = g.agent?.name || g.appName || g.name;
    const realm = g.realm?.name || 'its realm';
    return `When you give ${app} a tool, the tool reads and writes what it asks for, in ${realm}. ${app} sees only what its own access allows.`;
}

export default function AccessSheet({ pluginId, pluginName, groupId, grant: given, gate, verified = true, onClose, onSaved }) {
    const confirm = useConfirm();
    const { to, navigate } = usePlace();
    const [grant, setGrant] = useState(given);
    const [busy, setBusy] = useState(null);
    const [said, setSaid] = useState('');
    const [error, setError] = useState(null);
    const app = grant.agent?.name || grant.appName || grant.name;

    const choose = async (t, mode) => {
        if (mode === t.mode) return;
        if (mode === 'allow' && t.irreversible) {
            const ok = await confirm({
                title: `Let ${app} use ${t.name} without asking?`,
                body: `${t.name} does something that can’t be undone. Each time ${app} uses it, it goes ahead without asking you.`,
                label: 'Allow',
                danger: false,
            });
            if (!ok) return;
        }
        setBusy(t.toolId); setError(null); setSaid('');
        try {
            const r = await pluginsApi.setAccess(pluginId, groupId, grant.epId, { [t.toolId]: mode });
            const next = r?.ep_id ? accessItem(r) : null;
            // the server's row, keeping what an older one leaves out (what each tool reads and writes)
            const keep = (x) => {
                const was = grant.tools.find((y) => y.toolId === x.toolId);
                return { ...x, reads: x.reads ?? was?.reads ?? null, writes: x.writes ?? was?.writes ?? null };
            };
            const merged = next
                ? { ...next, tools: next.tools.map(keep) }
                : { ...grant, tools: grant.tools.map((x) => (x.toolId === t.toolId ? { ...x, mode, set: 'contract' } : x)) };
            setGrant(merged);
            setSaid(`${t.name}: ${ACCESS_WORD[mode]}.`);
            onSaved?.(merged);
        } catch (e) {
            setError(e?.message || 'Could not save');
        } finally {
            setBusy(null);
        }
    };

    return (
        <Modal open onClose={onClose} title="Tool access" size="md">
            <div className="plugins-form">
                <Facts items={[
                    { label: grant.kind === 'agent' ? 'Agent' : 'App', value: grant.name },
                    { label: 'Plugin', value: pluginName },
                    { label: 'Runs in', value: grant.realm?.name || '—' },
                ]} />
                {!verified && <Note tone="warn">{NOT_VERIFIED}</Note>}
                {verified && <p className="plugins-access__rule">{accessRule(grant)}</p>}
                {verified && (
                    <div className="plugins-access" role="list" aria-label="Its tools">
                        {grant.tools.map((t) => {
                            const line = toolAsksLine({ reads: t.reads, writes: t.writes });
                            return (
                                <div key={t.toolId || 'tool'} className="plugins-access__tool" role="listitem">
                                    <span className="plugins-access__name">
                                        <b>{t.name}</b>
                                        {(line || t.irreversible) && (
                                            <small>
                                                {line}
                                                {line && t.irreversible && ' · '}
                                                {t.irreversible && <span className="is-warn">Can’t be undone</span>}
                                            </small>
                                        )}
                                    </span>
                                    {gate.isAdmin
                                        ? <Segmented size="sm" label={`${t.name}: tool access for ${app}`} options={OPTIONS} value={t.mode} onChange={(m) => choose(t, m)} />
                                        : <span className="plugins-access__word">{ACCESS_WORD[t.mode] || ACCESS_WORD.deny}</span>}
                                    {busy === t.toolId && <span className="nx-sr">Saving</span>}
                                </div>
                            );
                        })}
                    </div>
                )}
                {!gate.isAdmin && <p className="plugins-held">{gate.hint}</p>}
                {error && <p className="plugins-error" role="alert">{error}</p>}
                <p className="nx-sr" aria-live="polite">{said}</p>
                <SheetFoot left={(
                    <button type="button" className="nx-link" onClick={() => navigate(to('realms', 'profiles', `open=${encodeURIComponent(grant.epId)}`))}>
                        Open the contract
                    </button>
                )}>
                    <FootButton primary onClick={onClose}>Done</FootButton>
                </SheetFoot>
            </div>
        </Modal>
    );
}
